import { useEffect } from "react";
import { toast } from "sonner";
import { pushSupported, subscribeToPush } from "@/lib/push";

const STORAGE_KEY = "dftb26.push.auto_prompted";

/**
 * The conference is a 3-day event with last-minute room swaps and on-the-day
 * announcements - we want every delegate subscribed without making them hunt
 * down the toggle. So on first authenticated app open we auto-prompt for
 * notification permission.
 *
 * Browser rules:
 *  - Permission can only be requested in response to a real user gesture OR
 *    immediately after page load, depending on the engine. We try the latter.
 *  - We only call requestPermission() once per browser (tracked in
 *    localStorage). Subsequent visits respect the user's first choice; they
 *    can flip it via Info → Notifications at any time.
 *  - iOS Safari only exposes Push when the app is installed to the home
 *    screen, so we skip the prompt in regular Safari to avoid an error toast.
 */
export function usePushAutoPrompt(user) {
  useEffect(() => {
    if (!user || !user.id) return;
    if (!pushSupported()) return;
    if (Notification.permission !== "default") return; // already decided
    if (localStorage.getItem(STORAGE_KEY) === "1") return;
    // Tiny delay so the browser's native permission prompt doesn't collide
    // with the auth redirect / first-render layout shift.
    const t = setTimeout(async () => {
      localStorage.setItem(STORAGE_KEY, "1");
      try {
        await subscribeToPush();
        toast.success("Notifications on - you'll get live alerts from organisers.", { duration: 4000 });
      } catch (err) {
        // Permission denied or PushManager unavailable - stay silent so we
        // don't pester the user. They can opt in later from Info.
        console.warn("[push/auto-prompt] subscription skipped:", err?.message || err);
      }
    }, 800);
    return () => clearTimeout(t);
  }, [user?.id]);
}
